import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchWarehouseStatusLineSeedThunk } from "../../store/reports/thunk";
import "../../styles/global.css";
import { setDate } from "../../store/reports/actions";
import {
  Home,
  PauseCircle,
  PlayCircle,
  ChevronLeftCircle,
  ChevronRightCircle,
} from "lucide-react";
import { fetchWarehouseThunk } from "../../store/redux/thunk";
import { APP_CONSTANTS } from "../../utilities/app_constants";

const StockPositionReport = ({
  reportData,
  onNext,
  onPrevious,
  onPause,
  isPaused,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params, setParams] = useState({});
  const { seedLines, currentDate, loading, error } = useSelector(
    (state) => state.report
  );
  const { warehouse } = useSelector((state) => state.filter);

  useEffect(() => {
    if (reportData?.url) {
      const urlParams = new URLSearchParams(reportData.url.split("?")[1]);
      const extractedParams = {
        warehouseId: urlParams.get("M_Warehouse_ID"),
        note: urlParams.get("Note"),
      };
      setParams(extractedParams);
    }
  }, [reportData]);

  useEffect(() => {
    if (params.warehouseId) {
      dispatch(fetchWarehouseThunk(params.warehouseId));
      dispatch(setDate(new Date().toISOString().split("T")[0]));
      dispatch(fetchWarehouseStatusLineSeedThunk(params.warehouseId));
    }
  }, [dispatch, params]);

  // Auto-Navigate after interval if not paused
  useEffect(() => {
    if (!isPaused) {
      const intervalInSeconds = parseInt(reportData?.interval, 10);
      const displayTime =
        !isNaN(intervalInSeconds) && intervalInSeconds > 0
          ? intervalInSeconds * 1000
          : APP_CONSTANTS.REPORT_DISPLAY_TIME;

      const timeout = setTimeout(() => {
        onNext();
      }, displayTime);

      return () => clearTimeout(timeout);
    }
  }, [onNext, isPaused, reportData?.interval]);

  // Add responsive layout styles WITH sticky header implementation
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      /* ONLY RESPONSIVE LAYOUT CHANGES - NO UI/COLOR CHANGES */
      
      /* Container responsiveness */
      .container {
        max-width: 100%;
        overflow-x: hidden;
      }
  
      /* Header responsive layout */
      @media (max-width: 1024px) {
        .header {
          flex-wrap: wrap !important;
          justify-content: center !important;
        }
        
        .header .home-button {
          order: 1;
        }
        
        .header .controls {
          order: 2;
        }
        
        .header h1 {
          order: 3;
          width: 100%;
          text-align: center;
          margin: 10px 0 !important;
        }
        
        .header .header-info {
          order: 4;
          width: 100%;
          justify-content: center !important;
          margin: 8px 0 !important;
        }
        
        .header .date-display {
          order: 5;
          margin: 8px auto !important;
        }
        
        .header .logo-container {
          order: 6;
          margin: 5px 0 !important;
        }
      }
      
      @media (max-width: 768px) {
        .header .controls svg {
          width: 32px !important;
          height: 32px !important;
        }
        
        .header h1 {
          font-size: 18px !important;
        }
        
        .current-date {
          width: 70px !important;
          font-size: 11px !important;
        }
      }
      
      @media (max-width: 480px) {
        .header .controls {
          gap: 5px !important;
        }
        
        .header .controls svg {
          width: 28px !important;
          height: 28px !important;
        }
        
        .header h1 {
          font-size: 16px !important;
        }
        
        .current-date {
          width: 60px !important;
          font-size: 10px !important;
        }
      }
      
      /* Table responsiveness - horizontal scroll */
      .stock-table-container {
        overflow-x: auto;
        width: 100%;
        margin: 20px 2px;
      }
      
      .stock-data-table {
        min-width: 600px;
      }
      
      /* Responsive table cell styles */
      @media (max-width: 768px) {
        .stock-data-table th,
        .stock-data-table td {
          padding: 8px !important;
          font-size: 12px !important;
        }
        
        .stock-data-table th {
          padding: 8px !important;
        }
      }
      
      @media (max-width: 480px) {
        .stock-data-table th,
        .stock-data-table td {
          padding: 6px !important;
          font-size: 10px !important;
        }
      }
      
      /* Marquee responsiveness */
      @media (max-width: 768px) {
        .marquee-container {
          font-size: 14px !important;
          bottom: 20px !important;
        }
        
        .marquee-container div span {
          padding-right: 60px !important;
        }
      }
      
      @media (max-width: 480px) {
        .marquee-container {
          font-size: 11px !important;
          bottom: 10px !important;
          padding: 8px 0 !important;
        }
        
        .marquee-container div span {
          padding-right: 40px !important;
        }
      }
      
      /* Keep ALL original table styles exactly as they were */
      .stock-table-container {
        overflow-x: auto;
        margin: 20px 2px;
        border: 1px solid #ddd;
        border-radius: 4px;
        position: relative;
      }
      
      .stock-data-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 14px;
      }
      
      .stock-data-table th,
      .stock-data-table td {
        border: 1px solid #ddd;
        padding: 10px;
        text-align: center;
        vertical-align: middle;
      }
      
      .stock-data-table th {
        background-color: #f2f2f2;
        font-weight: bold;
            color:#000;
      }
      
      /* ===== STICKY HEADER IMPLEMENTATION ===== */
      /* Blue Main Header - Sticky at top */
      .stock-data-table thead tr:first-child th {
        position: sticky;
        top: 0;
        z-index: 20;
        background-color: #06c!important;
      }
      
      /* Column Headers Row - Sticky below main header */
      .stock-data-table thead tr:nth-child(2) th {
        position: sticky;
        top: 60px; /* Height of first header row */
        z-index: 15;
     background:  #0070C0 !important;
            color:#ffff;
      }
      
      /* Ensure proper background on sticky headers */
      .stock-data-table thead tr:first-child th[colspan] {
        background-color:  #0066cc !important;
      }
      
      /* Add shadow for better visual separation when scrolling */
      .stock-data-table thead tr:first-child th {
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      .stock-data-table thead tr:nth-child(2) th {
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      /* Make the table container the scrolling context */
      .stock-table-container {
        overflow-y: auto;
        max-height: calc(100vh - 100px);
        position: relative;
      }
      
      /* Ensure table header stays within container */
      .stock-data-table thead {
        position: sticky;
        top: 0;
        z-index: 10;
      }
      
      .loading, .error {
        text-align: center;
        padding: 40px;
        font-size: 18px;
      }
      
      .no-data {
        text-align: center;
        padding: 40px;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Get unique material types
  const materialTypes = seedLines && seedLines.length > 0 
    ? [...new Set(seedLines.map((item) => item.typeofmaterial))]
    : [];

  // Get unique products
  const products = seedLines && seedLines.length > 0
    ? [...new Set(seedLines.map((item) => item.product))]
    : [];

  // Calculate total columns for colSpan
  const totalColumns = 1 + materialTypes.length;

  // Scrolling text content
  const scrollingText = `${params?.note || ""}`;

  return (
    <div className="container" style={{ position: "relative", minHeight: "100vh" }}>
      <header
        className="header"
        style={{
          flexWrap: "wrap",
          gap: "1px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <button
          className="home-button"
          onClick={() =>
            navigate("/menu", {
              state: { hideModal: true },
            })
          }
        >
          <Home
            className="home-icon"
            size={120}
            style={{
              width: "35px",
              height: "40px",
            }}
          />
        </button>
        
        <div
          className="controls"
          style={{ display: "flex", gap: "1px", alignItems: "center" }}
        >
          <ChevronLeftCircle
            size={40}
            onClick={onPrevious}
            style={{ cursor: "pointer" }}
          />
          {isPaused ? (
            <PlayCircle
              size={40}
              onClick={() => onPause(false)}
              style={{ cursor: "pointer" }}
            />
          ) : (
            <PauseCircle
              size={40}
              onClick={() => onPause(true)}
              style={{ cursor: "pointer" }}
            />
          )}
          <ChevronRightCircle
            size={40}
            onClick={onNext}
            style={{ cursor: "pointer" }}
          />
        </div>
        
        <h1 style={{ fontSize: "clamp(16px, 4vw, 28px)", margin: "5px 0" }}>
          Stock Position Seed
        </h1>
        
        <div className="header-info">
          <span
            style={{
              color: "#FFEB3B",
              fontSize: "clamp(16px, 3vw, 28px)",
              fontWeight: "bold",
            }}
          >
            <span
              style={{
                background: "linear-gradient(90deg, #0A2A88, #1565C0)",
                color: "#FFFFFF",
                padding: "6px 9px",
                borderRadius: "30px",
                fontSize: "clamp(14px, 2vw, 28px)",
                fontWeight: "800",
                display: "inline-flex",
                alignItems: "center",
                gap: "10px",
                border: "2px solid rgba(255,255,255,0.7)",
                letterSpacing: "0.5px",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  color: "#BBDEFB",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  opacity: 0.9,
                }}
              >
                Warehouse
              </span>

              <span
                style={{
                  width: "2px",
                  height: "22px",
                  background: "rgba(255,255,255,0.5)",
                }}
              />

              <span
                style={{
                  color: "#FFFFFF",
                  wordBreak: "break-word",
                  maxWidth: "400px",
                  fontWeight: "900",
                  whiteSpace: "normal",
                  textShadow: "0 0 8px black, 0 0 12px black",
                }}
              >
                {warehouse?.data?.length > 0
                  ? warehouse.data[0].name
                  : "-"}
              </span>
            </span>
          </span>
        </div>
        
        <div
          className="date-display"
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <span>Current Date:</span>
          <input
            type="text"
            value={currentDate}
            readOnly
            className="current-date"
            style={{
              width: "80px",
              minWidth: "80px",
              padding: "4px 8px",
              fontSize: "14px",
            }}
          />
        </div>
        
        <div className="logo-container">
          <a
            href="https://www.digiopenservices.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/Logo.png"
              alt="Logo"
              className="header-logo"
              style={{
                maxwidth: "110px",
                height: "40px",
                objectFit: "contain",
                borderRadius: "6px",
                margin: "2px 10px",
                cursor: "pointer",
              }}
            />
          </a>
        </div>
      </header>
      
      {/* {loading && <p className="loading">Loading data, please wait...</p>} */}
      {error && <p className="error">{error}</p>}
      
      {!loading && (!seedLines || seedLines.length === 0) && (
        <div className="no-data">
          <p>No stock position data available.</p>
        </div>
      )}
      
      {!loading && seedLines && seedLines.length > 0 && (
        <div className="stock-table-container">
          <table className="stock-data-table">
            <thead>
              
              <tr>
                <th
                  colSpan={totalColumns}
                  style={{
                    padding: "10px",
                    border: "1px solid #ccc",
                    backgroundColor: "#0070C0",
                    textAlign: "center",
                    fontSize: "clamp(18px, 3vw, 30px)",
                    fontWeight: "bold",
                    color: "white",
                  }}
                >
                  Stock Position - Seed
                </th>
              </tr>
              
              {/* Column Headers Row (Grey - Sticky) */}
              <tr>
                <th
                  style={{
                    padding: "12px",
                    border: "1px solid #ccc",
                    whiteSpace: "nowrap",
                    fontSize: "17px",
                    backgroundColor: "#f2f2f2",
                    color:"#fff",
                    minWidth: "150px",
                  }}
                >
                  Product
                </th>
                {materialTypes.map((material, index) => (
                  <th
                    key={index}
                    style={{
                      padding: "12px",
                      border: "1px solid #ccc",
                      whiteSpace: "nowrap",
                      textAlign: "center",
                      fontSize: "17px",
                      backgroundColor: "#f2f2f2",
                        color:"#fff",
                      minWidth: "120px",
                    }}
                  >
                    {material}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map((product, rowIndex) => {
                return (
                  <tr key={rowIndex}>
                    <td
                      style={{
                        padding: "12px",
                        border: "1px solid #ccc",
                        whiteSpace: "nowrap",
                        fontSize: "17px",
                        fontWeight: "bold",
                        backgroundColor: "#fafafa",
                        minWidth: "150px",
                      }}
                    >
                      {product}
                    </td>
                    {materialTypes.map((material, colIndex) => {
                      const entry = seedLines.find(
                        (item) =>
                          item.product === product &&
                          item.typeofmaterial === material
                      );
                      return (
                        <td
                          key={colIndex}
                          style={{
                            padding: "12px",
                            border: "1px solid #ccc",
                            whiteSpace: "nowrap",
                            textAlign: "center",
                            fontSize: "17px",
                            minWidth: "120px",
                          }}
                        >
                          {entry ? entry.pendingqtyactual : "-"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div
        style={{
          position: "fixed",
          bottom: "40px",
          left: 0,
          right: 0,
          color: "#022fe3",
          padding: "12px 0",
          overflow: "hidden",
          whiteSpace: "nowrap",
          fontFamily: "monospace",
          fontSize: "25px",
          fontWeight: "700",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            display: "inline-block",
            animation: "scrollRightToLeft 35s linear infinite",
            whiteSpace: "nowrap",
            paddingLeft: "100%",
          }}
        >
          <span>{scrollingText}</span>
        </div>

        <style>
          {`
      @keyframes scrollRightToLeft {
        0% {
          transform: translateX(0%);
        }
        100% {
          transform: translateX(-100%);
        }
      }
    `}
        </style>
      </div>
    </div>
  );
};

export default StockPositionReport;